import {
	IsArray,
	IsIn,
	IsInt,
	IsObject,
	IsOptional,
	IsString,
	MaxLength,
	ValidateIf,
} from 'class-validator';
import type { ListOverrideAudience } from '../entity/list-override.entity';

/**
 * List overrides API (PD-14278).
 *
 * The API speaks FULL arrays — the columns / filter chips exactly as the
 * editor shows them — and the server does the delta arithmetic against the
 * layer beneath the one being saved. Storage is deltas; the wire is what a
 * human sees. `ListConfiguration`, `ColumnConfiguration` and the provenance
 * shapes come from `@supply-mover-2-0/core`; they are `unknown` here so this
 * library stays free of a core dependency.
 */

/** Identifies one override slot: scope × audience. */
export class ListOverrideSlotDto {
	@IsString()
	@MaxLength(100)
	public listKey!: string;

	/** Bundled list id, or null for an entity-wide override. */
	@ValidateIf((o: ListOverrideSlotDto) => o.listId != null)
	@IsString()
	@MaxLength(100)
	public listId!: string | null;

	@IsString()
	@IsIn(['organization', 'role'])
	public audience!: ListOverrideAudience;

	/** Required when `audience` is 'role'. */
	@ValidateIf((o: ListOverrideSlotDto) => o.audience === 'role')
	@IsInt()
	public roleId!: number | null;
}

/**
 * Save one or more facets of an override. A facet left `undefined` is not
 * touched; `null` clears that facet's delta (revert to the layer beneath).
 */
export class SaveListOverrideDto extends ListOverrideSlotDto {
	/**
	 * For an entity-wide slot: the bundled list the editor was looking at
	 * while making the change. The delta is diffed against THAT list (as it
	 * stands beneath the slot) and then applies to every list of the entity.
	 */
	@IsOptional()
	@IsString()
	@MaxLength(100)
	public viaListId?: string;

	/** Full column list as edited (`Array<ColumnConfiguration>`). */
	@IsOptional()
	@ValidateIf((o: SaveListOverrideDto) => o.columns != null)
	@IsArray()
	public columns?: Array<unknown> | null;

	/** Full filter-chip id list as edited. */
	@IsOptional()
	@ValidateIf((o: SaveListOverrideDto) => o.activeFilters != null)
	@IsArray()
	@IsString({ each: true })
	public activeFilters?: Array<string> | null;

	/**
	 * Per-filter rule fields to change (`Record<filterId, Partial<FilterRule>>`).
	 * Stored as given — filter rules were already a field-by-field merge.
	 */
	@IsOptional()
	@ValidateIf((o: SaveListOverrideDto) => o.filterRules != null)
	@IsObject()
	public filterRules?: Record<string, unknown> | null;

	@IsOptional()
	@ValidateIf((o: SaveListOverrideDto) => o.defaultSortId != null)
	@IsString()
	public defaultSortId?: string | null;

	/** Curated sort options (`Array<TableSortOption>`), replaced wholesale. */
	@IsOptional()
	@ValidateIf((o: SaveListOverrideDto) => o.sortOptions != null)
	@IsArray()
	public sortOptions?: Array<unknown> | null;

	/** Misc per-list knobs, merged key by key. */
	@IsOptional()
	@ValidateIf((o: SaveListOverrideDto) => o.listOverrides != null)
	@IsObject()
	public listOverrides?: Record<string, unknown> | null;
}

export type ListOverrideFacet =
	| 'columns'
	| 'filters'
	| 'filterRules'
	| 'defaultSortId'
	| 'sortOptions'
	| 'listOverrides';

/** One stored override row, summarized for the editor's variant picker. */
export interface ListOverrideSummaryDto {
	id: number;
	listKey: string;
	listId: string | null;
	audience: ListOverrideAudience;
	roleId: number | null;
	roleName: string | null;
	/** Which facets this override changes. */
	facets: Array<ListOverrideFacet>;
	modifiedDate: string | null;
}

/**
 * A list resolved for the editor of one slot: the list as it stands with
 * every layer up to AND including that slot applied, plus the base the slot
 * is diffed against, so the editor can show what this layer changed.
 */
export interface ListOverrideEditorViewDto {
	slot: {
		listKey: string;
		listId: string | null;
		audience: ListOverrideAudience;
		roleId: number | null;
		roleName: string | null;
	};
	/** `ListConfiguration` after this slot. */
	list: unknown;
	activeFilters: Array<string>;
	/** `ListConfiguration` before this slot — what the editor started from. */
	base: unknown;
	baseActiveFilters: Array<string>;
	/** `ListProvenance` through this slot. */
	provenance: unknown;
	/** Whether a row exists for this slot yet. */
	exists: boolean;
}

/** Save the tab strip for an audience. `tabIds` is the strip as shown. */
export class SaveTabStripDto {
	@IsString()
	@MaxLength(100)
	public listKey!: string;

	@IsString()
	@IsIn(['organization', 'role'])
	public audience!: ListOverrideAudience;

	@ValidateIf((o: SaveTabStripDto) => o.audience === 'role')
	@IsInt()
	public roleId!: number | null;

	@IsArray()
	@IsString({ each: true })
	public tabIds!: Array<string>;
}

export interface TabStripSummaryDto {
	id: number;
	listKey: string;
	audience: ListOverrideAudience;
	roleId: number | null;
	roleName: string | null;
	tabOrder: Array<string> | null;
	hiddenIds: Array<string>;
}

/** The tab strip resolved for the editor of one audience. */
export interface TabStripEditorViewDto {
	audience: ListOverrideAudience;
	roleId: number | null;
	roleName: string | null;
	/** Strip after this audience's delta. */
	tabIds: Array<string>;
	/** Strip before it. */
	baseTabIds: Array<string>;
	/** Tabs available to add (bundled lists not shown + shareable saved lists). */
	availableTabIds: Array<string>;
	exists: boolean;
}

/**
 * Everything a list page needs, resolved for one viewer: every bundled list
 * of the entity with overrides applied, the configuration-level chip set,
 * the tab strip, and per-list provenance for the preview.
 */
export interface ResolvedListConfigDto {
	listKey: string;
	viewer: {
		userId: number;
		roleIds: Array<number>;
		roleNames: Array<string>;
	};
	/** `Array<ListConfiguration>` — bundled lists (active and inactive). */
	lists: Array<unknown>;
	/** Configuration-level `filters.activeFilters`. */
	activeFilters: Array<string>;
	/** `Record<listId, ListProvenance>`. */
	provenance: Record<string, unknown>;
	tabStrip: {
		tabIds: Array<string>;
		/** `Record<tabId, TabProvenance>` */
		provenance: Record<string, unknown>;
		/** `Array<LayerRef>` */
		layers: Array<unknown>;
		/** Whether the viewer has their own strip and it is enabled. */
		isCustom: boolean;
		isEnabled: boolean;
	};
	/** Tenant role ladder, highest first. */
	roleLadder: Array<{ id: number; name: string; precedence: number | null }>;
}

export class SaveRoleLadderDto {
	/** Role ids highest-precedence first. Roles omitted become unranked. */
	@IsArray()
	@IsInt({ each: true })
	public roleIds!: Array<number>;
}
